// frontend/src/pages/CheckoutPage.jsx
// Updated with guest checkout support + tiered token redemption + Loyalty 2.0
// UPDATED Dec 31, 2025: Enhanced pickup incentive - 10% token bonus + $5.99 savings display (4.7)
// UPDATED Jan 15, 2026: B14 - Added "Continue Shopping" button on Step 1
// UPDATED Jan 15, 2026: B15 - Added time window selection UX in checkout
// UPDATED: Added platform fee (7.5%), logistics fee (7.5%), and CA sales tax display
// Guest users can checkout with just email - no account required

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Truck, Star, Check, ShoppingCart, AlertCircle, Coins, Info, ChevronRight, User, Mail, Award, Gift, Sparkles, Clock, Calendar } from 'lucide-react';
import { useMutation, useQuery, gql } from '@apollo/client';
import { Elements } from '@stripe/react-stripe-js';
import { useCart } from '../context/CartContext';
import { useNotification } from '../context/NotificationContext';
import { useStripe as useStripeContext } from '../context/StripeContext';
import { useTokens } from '../context/TokenContext';
import { CREATE_PURCHASE, CREATE_PAYMENT_INTENT, CONFIRM_PAYMENT } from '../graphql/payment';
import { GET_CURRENT_USER } from '../apollo/queries';
import StripeCheckout from '../components/payments/StripeCheckout';
import Tooltip, { 
  LoyaltyTierTooltip, 
  RedemptionCapTooltip,
  PickupBonusTooltip 
} from '../components/common/Tooltip';
import {
  TOKEN_CONFIG,
  calculateMaxRedemption,
  applyTokenRedemption,
  getNextLoyaltyTier,
  getRedemptionDisplay,
  getTierProgress,
  calculateLoyaltyTier,
  formatTokens,
  formatTokenValue,
  tokensToUSD
} from '../utils/tokenEconomy';
import { useAchievements } from '../context/AchievementContext';

// Query to get available groups
const GET_GROUPS = gql`
  query GetGroups {
    groups {
      id
      name
      status
    }
  }
`;

// Standard delivery fee (shown as savings when user picks up)
const STANDARD_DELIVERY_FEE = 5.99;

// Pickup bonus configuration
const PICKUP_BONUS_CONFIG = {
  percentOfBase: 10,  // 10% of base tokens
  minimumBonus: 5,    // At least 5 tokens
  maximumBonus: 50    // Cap at 50 tokens for very large orders
};

// Time window configuration
const TIME_WINDOWS = {
  EXPRESS: { label: 'Express (Same Day)', days: 0, description: 'Get it today', icon: '⚡' },
  ONE_DAY: { label: '1-Day', days: 1, description: 'Next business day', icon: '🚀' },
  THREE_DAY: { label: '3-Day', days: 3, description: 'Best savings potential', icon: '💰', recommended: true },
  WEEKLY: { label: 'Weekly', days: 7, description: 'Maximum group discount', icon: '📦' }
};

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems, clearCart, getCartTotals, updateCartItemTimeWindow } = useCart();
  const { addNotification } = useNotification();
  const { isConfigured, stripePromise } = useStripeContext();
  const { balance: tokenBalance, refreshBalance } = useTokens();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  const [currentPurchaseId, setCurrentPurchaseId] = useState(null);
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const { celebrateAchievements } = useAchievements();
  
  // Token redemption state
  const [tokensToRedeem, setTokensToRedeem] = useState(0);
  const [showTokenInfo, setShowTokenInfo] = useState(false);
  
  // Time window editing state (B15)
  const [editingTimeWindow, setEditingTimeWindow] = useState(null); // itemId being edited
  const [showTimeWindowInfo, setShowTimeWindowInfo] = useState(true); // Expanded by default
  
  // Check if user is authenticated
  const { data: userData, loading: userLoading } = useQuery(GET_CURRENT_USER, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore' // Don't fail if not authenticated
  });
  
  // Query for available groups
  const { data: groupsData } = useQuery(GET_GROUPS, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore'
  });
  
  const isAuthenticated = !!userData?.me;
  const currentUser = userData?.me;
  const availableGroups = groupsData?.groups || [];
  
  // Calculate user's loyalty tier from activity or use stored value
  const userPurchases = currentUser?.totalPurchases || 0;
  const userReferrals = currentUser?.successfulReferrals || 0;
  const calculatedTierInfo = calculateLoyaltyTier(userPurchases, userReferrals);
  const userLoyaltyTier = currentUser?.loyaltyTier || calculatedTierInfo.tier || 'NEW_USER';
  
  // Get tier progress for display
  const tierProgress = useMemo(() => 
    getTierProgress(userPurchases, userReferrals),
    [userPurchases, userReferrals]
  );
  
  // Get tier cap info for HYBRID display
  const tierCapInfo = TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS[userLoyaltyTier] || 
                      TOKEN_CONFIG.LOYALTY_REDEMPTION_CAPS.NEW_USER;
  
  // GraphQL mutations
  const [createPurchase] = useMutation(CREATE_PURCHASE);
  const [createPaymentIntent] = useMutation(CREATE_PAYMENT_INTENT);
  const [confirmPayment] = useMutation(CONFIRM_PAYMENT);
  
  // Form states
  const [deliveryMethod, setDeliveryMethod] = useState('pickup');
  
  const [billingInfo, setBillingInfo] = useState({
    name: '',
    email: '',
    phone: '',
    deliveryAddress: {
      address: '',
      city: '',
      state: '',
      zip: ''
    }
  });

  // Fee breakdown returned from backend after createPaymentIntent
  // Use these for display — backend is the single source of truth for all fee math
  const [backendFees, setBackendFees] = useState(null);

  // Pre-fill billing info if user is authenticated
  useEffect(() => {
    if (currentUser) {
      setBillingInfo(prev => ({
        ...prev,
        name: currentUser.name || prev.name,
        email: currentUser.email || prev.email,
        phone: currentUser.phone || prev.phone
      }));
    }
  }, [currentUser]);

  // Calculate subtotal directly from cart items as fallback
  const calculatedSubtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const itemPrice = item.price || item.unitPrice || item.currentPrice || item.basePrice || item.productPrice || 0;
      const itemQty = item.quantity || item.qty || 1;
      // Apply bulk discount to match getCartTotals() in CartContext
      let discount = 0;
      if (itemQty >= 5) discount = 0.05;
      else if (itemQty >= 3) discount = 0.03;
      const discountedPrice = itemPrice * (1 - discount);
      return sum + (discountedPrice * itemQty);
    }, 0);
  }, [cartItems]);

  // Get cart totals - use fallback if getCartTotals returns 0
  const cartTotals = getCartTotals();
  const subtotal = cartTotals.subtotal > 0 ? cartTotals.subtotal : calculatedSubtotal;
  const deliveryFee = 0; // Free delivery during launch promo
  const subtotalWithDelivery = subtotal + deliveryFee;
  
  // Only allow token redemption for authenticated users
  const effectiveTokenBalance = isAuthenticated ? (tokenBalance || 0) : 0;
  
  // Calculate token redemption with loyalty tier (HYBRID system)
  const redemptionInfo = useMemo(() => 
    calculateMaxRedemption(subtotalWithDelivery, effectiveTokenBalance, userLoyaltyTier),
    [subtotalWithDelivery, effectiveTokenBalance, userLoyaltyTier]
  );
  
  // Get the actual max tokens that can be redeemed (safe fallback to 0)
  const maxRedeemableTokens = redemptionInfo?.actualMaxTokens || 0;
  
  const redemptionDisplay = useMemo(() => 
    getRedemptionDisplay(subtotalWithDelivery, effectiveTokenBalance, userLoyaltyTier),
    [subtotalWithDelivery, effectiveTokenBalance, userLoyaltyTier]
  );
  
  const nextTierInfo = useMemo(() => 
    getNextLoyaltyTier(userLoyaltyTier),
    [userLoyaltyTier]
  );
  
  // Calculate actual discount from tokens
  const tokenDiscount = useMemo(() => {
    if (tokensToRedeem <= 0 || !isAuthenticated) return 0;
    const result = applyTokenRedemption(subtotalWithDelivery, tokensToRedeem, effectiveTokenBalance, userLoyaltyTier);
    return result.discountAmount || 0;
  }, [tokensToRedeem, subtotalWithDelivery, effectiveTokenBalance, isAuthenticated, userLoyaltyTier]);
  
  // Final total (before fees — kept for token logic compatibility)
  const total = Math.max(0, subtotalWithDelivery - tokenDiscount);

  // ─── Platform + Logistics Fee Display Calculation ─────────────────────────
  // Mirrors backend calculateFees() — display only, backend re-validates on charge
  // Fees are calculated on the post-token-discount subtotal (matches backend behavior)
  const feeBase = Math.max(0, subtotalWithDelivery - tokenDiscount);
  const platformFee = useMemo(() =>
    backendFees ? backendFees.platformFee : Math.round(feeBase * 0.075 * 100) / 100,
    [feeBase, backendFees]
  );
  const logisticsFee = useMemo(() =>
    backendFees ? backendFees.logisticsFee : Math.round(feeBase * 0.075 * 100) / 100,
    [feeBase, backendFees]
  );
  const salesTax = useMemo(() =>
    backendFees ? backendFees.tax : Math.round(feeBase * 0.0725 * 100) / 100,
    [feeBase, backendFees]
  );
  const totalWithFees = backendFees
    ? backendFees.total
    : Math.max(0, feeBase + platformFee + logisticsFee + salesTax);
  // ─────────────────────────────────────────────────────────────────────────
  
  // Group cart items by time window (B15)
  const itemsByTimeWindow = useMemo(() => {
    const grouped = {};
    cartItems.forEach(item => {
      const tw = item.timeWindow || 'THREE_DAY';
      if (!grouped[tw]) {
        grouped[tw] = [];
      }
      grouped[tw].push(item);
    });
    return grouped;
  }, [cartItems]);
  
  // Check if cart has mixed time windows
  const hasMixedTimeWindows = Object.keys(itemsByTimeWindow).length > 1;
  
  // Calculate tokens to be earned - PRICE BASED (1 token per $1 spent)
  // UPDATED 4.7: Pickup bonus now 10% of base tokens (min 5, max 50)
  const tokenCalc = useMemo(() => {
    // Base: 1 token per $1 spent (10% back in tokens)
    const baseTokens = Math.floor(subtotal);
    
    // Bulk bonus based on cart VALUE, not quantity
    let bulkMultiplier = 1;
    let bulkBonusPercent = 0;
    if (subtotal >= 50) {
      bulkMultiplier = 1.5;  // 50% bonus for $50+ orders
      bulkBonusPercent = 50;
    } else if (subtotal >= 25) {
      bulkMultiplier = 1.25; // 25% bonus for $25+ orders
      bulkBonusPercent = 25;
    }
    
    const bulkTokens = Math.floor(baseTokens * bulkMultiplier);
    const bulkBonus = bulkTokens - baseTokens;
    
    // UPDATED: Pickup bonus = 10% of base tokens (min 5, max 50)
    const calculatedPickupBonus = Math.min(
      PICKUP_BONUS_CONFIG.maximumBonus,
      Math.max(
        PICKUP_BONUS_CONFIG.minimumBonus,
        Math.floor(baseTokens * (PICKUP_BONUS_CONFIG.percentOfBase / 100))
      )
    );
    const pickupBonus = deliveryMethod === 'pickup' ? calculatedPickupBonus : 0;
    const pickupBonusValue = pickupBonus * 0.10; // Dollar value of pickup bonus
    
    // First purchase bonus for new users
    const firstPurchaseBonus = !isAuthenticated ? 5 : 0;
    
    const totalTokens = bulkTokens + pickupBonus + firstPurchaseBonus;
    
    // Total pickup savings = delivery fee saved + token bonus value
    const totalPickupSavings = STANDARD_DELIVERY_FEE + pickupBonusValue;
    
    return {
      baseTokens,
      bulkMultiplier,
      bulkBonus,
      bulkBonusPercent,
      pickupBonus,
      pickupBonusValue,
      calculatedPickupBonus, // The bonus they WOULD get if they chose pickup
      totalPickupSavings,
      firstPurchaseBonus,
      totalTokens,
      totalValue: totalTokens * 0.10 // $0.10 per token
    };
  }, [subtotal, deliveryMethod, isAuthenticated]);

  // Handle token slider change
  const handleTokenSliderChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setTokensToRedeem(Math.min(value, maxRedeemableTokens));
  };
  
  // Quick select buttons
  const handleQuickSelect = (amount) => {
    if (amount === 'max') {
      setTokensToRedeem(maxRedeemableTokens);
    } else if (amount === 'none') {
      setTokensToRedeem(0);
    } else if (amount === 'half') {
      setTokensToRedeem(Math.floor(maxRedeemableTokens / 2));
    } else {
      setTokensToRedeem(Math.min(amount, maxRedeemableTokens));
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section === 'billing') {
      setBillingInfo(prev => ({ ...prev, [field]: value }));
    }
  };

  // Email validation
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle time window change for an item (B15)
  const handleTimeWindowChange = (itemId, groupId, newTimeWindow) => {
    if (updateCartItemTimeWindow) {
      updateCartItemTimeWindow(itemId, groupId, newTimeWindow);
    }
    setEditingTimeWindow(null);
    
    addNotification({
      type: 'success',
      title: 'Time Window Updated',
      message: `Delivery window changed to ${TIME_WINDOWS[newTimeWindow]?.label || newTimeWindow}`,
      showToast: true
    });
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return billingInfo.name.trim() && isValidEmail(billingInfo.email);
      case 2:
        return true; // Stripe handles validation
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (!validateStep()) {
      addNotification({
        type: 'error',
        title: 'Missing Information',
        message: 'Please enter your name and a valid email address.',
        showToast: true
      });
      return;
    }

    if (currentStep === 1) {
      try {
        setLoading(true);

        console.log('=== CHECKOUT DEBUG START ===');
        console.log('Cart items:', cartItems);
        console.log('Is authenticated (from query):', isAuthenticated);
        console.log('User loading:', userLoading);
        console.log('Current user:', currentUser);
        console.log('User loyalty tier:', userLoyaltyTier);
        console.log('Tokens to redeem:', tokensToRedeem);
        console.log('Token discount:', tokenDiscount);
        console.log('Pickup bonus:', tokenCalc.pickupBonus);
        
        // Check all possible auth token locations
        const possibleTokens = {
          token: localStorage.getItem('token'),
          authToken: localStorage.getItem('authToken'),
          accessToken: localStorage.getItem('accessToken'),
          jwt: localStorage.getItem('jwt'),
          userToken: localStorage.getItem('userToken'),
        };
        console.log('Possible tokens in localStorage:', possibleTokens);
        
        // Also check sessionStorage
        const sessionTokens = {
          token: sessionStorage.getItem('token'),
          authToken: sessionStorage.getItem('authToken'),
        };
        console.log('Possible tokens in sessionStorage:', sessionTokens);
        
        // Check if we have any token
        const hasToken = Object.values(possibleTokens).some(t => !!t) || 
                         Object.values(sessionTokens).some(t => !!t);
        
        console.log('Has any auth token:', hasToken);
        console.log('=== CHECKOUT DEBUG END ===');
        
        // If no token and no currentUser, show message (don't make API call)
        if (!hasToken && !currentUser) {
          addNotification({
            type: 'warning',
            title: 'Not Logged In',
            message: 'Please log in to complete your purchase. Click the profile icon to log in.',
            showToast: true
          });
          setLoading(false);
          return;
        }

        // Get groupId from cart items or fall back to first available group
        // Only include if it's a valid MongoDB ObjectId (24 hex characters)
        const cartGroupId = cartItems[0]?.groupId;
        const isValidCartGroupId = cartGroupId && /^[a-f\d]{24}$/i.test(cartGroupId);
        
        // Get first available group as fallback
        const fallbackGroupId = availableGroups[0]?.id;
        const isValidFallbackId = fallbackGroupId && /^[a-f\d]{24}$/i.test(fallbackGroupId);
        
        // Use cart groupId if valid, otherwise use fallback
        const groupIdToUse = isValidCartGroupId ? cartGroupId : (isValidFallbackId ? fallbackGroupId : null);
        
        console.log('Group ID from cart:', cartGroupId, 'valid:', isValidCartGroupId);
        console.log('Fallback group ID:', fallbackGroupId, 'valid:', isValidFallbackId);
        console.log('Using group ID:', groupIdToUse);
        console.log('Available groups:', availableGroups);

        // Note: groupId is optional - backend will auto-create groups based on timeWindow
        // If no group exists, purchase will create one automatically

        // Build purchase input
        const purchaseInput = {
          groupId: groupIdToUse || undefined, // Optional - backend creates groups automatically
          timeWindow: cartItems[0]?.timeWindow || 'THREE_DAY', // Default to 3-day window
          products: cartItems.map(item => ({
            productId: item.id,
            quantity: item.quantity || 1
          })),
          paymentMethod: 'card',
          logistics: {
            type: deliveryMethod === 'pickup' ? 'PICKUP' : 'DELIVERY',
            notes: ''
          }
        };
        
        console.log('Purchase input:', purchaseInput);

        // Create purchase
        const purchaseData = await createPurchase({
          variables: {
            input: purchaseInput
          }
        });
        
        console.log('Purchase created successfully:', purchaseData);
        const purchaseId = purchaseData.data.createPurchase.id;
        setCurrentPurchaseId(purchaseId);
        
        // Create payment intent with token redemption
        console.log('=== PAYMENT INTENT REQUEST ===');
        console.log('purchaseId:', purchaseId);
        console.log('amount (subtotalWithDelivery):', subtotalWithDelivery);
        console.log('tokenReward:', tokenCalc.totalTokens);
        console.log('tokenRedemption:', tokensToRedeem);
        
        const paymentData = await createPaymentIntent({
          variables: {
            purchaseId,
            amount: subtotalWithDelivery,
            tokenReward: tokenCalc.totalTokens,
            tokenRedemption: tokensToRedeem
          }
        });
        
        console.log('Payment intent response:', JSON.stringify(paymentData, null, 2));
        
        // NULL SAFETY CHECK - Prevents crash when createPaymentIntent fails
        if (!paymentData) {
          throw new Error('Payment service returned no response. Please try again.');
        }
        
        if (paymentData.errors && paymentData.errors.length > 0) {
          const errorMsg = paymentData.errors[0].message || 'Payment initialization failed';
          console.error('GraphQL errors in paymentData:', paymentData.errors);
          throw new Error(errorMsg);
        }
        
        if (!paymentData.data) {
          console.error('paymentData.data is null:', paymentData);
          throw new Error('Payment service error. Please try again.');
        }
        
        if (!paymentData.data.createPaymentIntent) {
          console.error('createPaymentIntent returned null:', paymentData.data);
          throw new Error('Could not initialize payment. Please try again or contact support.');
        }
        
        if (!paymentData.data.createPaymentIntent.clientSecret) {
          console.error('No clientSecret in response:', paymentData.data.createPaymentIntent);
          throw new Error('Payment setup incomplete. Please try again.');
        }
        
        setPaymentClientSecret(paymentData.data.createPaymentIntent.clientSecret);

        // Store backend fee breakdown — this becomes the single source of truth for all totals
        if (paymentData.data.createPaymentIntent.feeBreakdown) {
          setBackendFees(paymentData.data.createPaymentIntent.feeBreakdown);
        }
        
        setCurrentStep(prev => prev + 1);
      } catch (error) {
        console.error('Checkout error:', error);
        console.error('Error message:', error.message);
        console.error('GraphQL errors:', error.graphQLErrors);
        console.error('Network error:', error.networkError);
        
        // Get the actual error message
        let errorMessage = 'Failed to process order. Please try again.';
        
        if (error.graphQLErrors?.length > 0) {
          errorMessage = error.graphQLErrors[0].message;
        } else if (error.networkError?.result?.errors?.length > 0) {
          errorMessage = error.networkError.result.errors[0].message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        addNotification({
          type: 'error',
          title: 'Checkout Error',
          message: errorMessage,
          showToast: true
        });
      } finally {
        setLoading(false);
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate(-1);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      console.log('=== PAYMENT SUCCESS START ===');
      console.log('Payment Intent:', paymentIntent);
      console.log('Current Purchase ID:', currentPurchaseId);
      console.log('Is Guest Checkout:', isGuestCheckout);
      
      // Confirm payment on backend
      const result = await confirmPayment({
        variables: {
          paymentIntentId: paymentIntent.id,
          phone: billingInfo.phone || null,
          deliveryAddress: deliveryMethod === 'delivery' ? {
            address: billingInfo.deliveryAddress.address,
            city: billingInfo.deliveryAddress.city,
            state: billingInfo.deliveryAddress.state,
            zip: billingInfo.deliveryAddress.zip
          } : null,
          ...(isGuestCheckout && {
            guestInfo: {
              name: billingInfo.name,
              email: billingInfo.email,
              phone: billingInfo.phone
            }
          })
        }
      });
      
      console.log('Confirm Payment Result:', result);
      
      if (result.data.confirmPayment.success) {
        const purchaseId = currentPurchaseId || result.data.confirmPayment.purchase?.id;
        console.log('Final Purchase ID for navigation:', purchaseId);
        
        // Clear cart after successful order
        clearCart();
        
        // Refresh token balance (authenticated users only)
        if (isAuthenticated) {
          refreshBalance();
        }

        // Celebrate any new achievements!
        if (result.data.confirmPayment.newAchievements?.length > 0) {
          celebrateAchievements(result.data.confirmPayment.newAchievements);
        }
        
        // Show success notification with token info
        let tokenMessage = '';
        if (isAuthenticated) {
          const tokensEarned = result.data.confirmPayment.tokensEarned || tokenCalc.totalTokens;
          const tokensRedeemed = result.data.confirmPayment.tokensRedeemed || tokensToRedeem;
          const netTokens = tokensEarned - tokensRedeemed;
          
          if (tokensRedeemed > 0 && tokensEarned > 0) {
            tokenMessage = `Earned ${tokensEarned} tokens, used ${tokensRedeemed} (net: ${netTokens >= 0 ? '+' : ''}${netTokens})`;
          } else if (tokensRedeemed > 0) {
            tokenMessage = `Used ${tokensRedeemed} tokens for $${tokenDiscount.toFixed(2)} off`;
          } else {
            tokenMessage = `You earned ${tokensEarned} tokens!`;
          }
        } else {
          tokenMessage = 'Create an account to start earning tokens on future purchases!';
        }
        
        addNotification({
          type: 'success',
          title: 'Order Placed!',
          message: `Your order has been confirmed. ${tokenMessage}`,
          showToast: true
        });
        
        // Navigate to confirmation page
        if (purchaseId && !purchaseId.startsWith('guest_')) {
          console.log('Navigating to:', `/order-confirmation/${purchaseId}`);
          navigate(`/order-confirmation/${purchaseId}`);
        } else {
          console.log('Guest order - showing step 4');
          setOrderComplete(true);
          setCurrentStep(4);
        }
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      addNotification({
        type: 'error',
        title: 'Payment Error',
        message: 'Payment was processed but order confirmation failed. Please contact support.',
        showToast: true
      });
    }
  };

  // Check if cart is empty
  if (cartItems.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add some items to your cart to checkout</p>
          <button 
            onClick={() => navigate('/home')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // Helper: Get tier color
  const getTierColor = (tier) => {
    const colors = {
      'NEW_USER': 'bg-gray-100 text-gray-700 border-gray-300',
      'BRONZE': 'bg-amber-100 text-amber-800 border-amber-300',
      'SILVER': 'bg-slate-100 text-slate-700 border-slate-300',
      'GOLD': 'bg-yellow-100 text-yellow-800 border-yellow-400',
      'PLATINUM': 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 border-purple-300'
    };
    return colors[tier] || colors['NEW_USER'];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center">
          <button 
            onClick={handleBack}
            className="mr-3 p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-semibold">
              {orderComplete ? 'Order Confirmed!' : 'Checkout'}
            </h1>
            {!orderComplete && (
              <p className="text-sm text-gray-600">Step {currentStep} of 3</p>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {!orderComplete && (
        <div className="bg-white px-4 py-3 border-b">
          <div className="flex items-center">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step < currentStep ? <Check size={16} /> : step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-600">
            <span>Details</span>
            <span>Payment</span>
            <span>Confirm</span>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto">
        {/* Step 1: Contact Information */}
        {currentStep === 1 && (
          <div className="space-y-4 m-4">
            {/* Guest Checkout Notice */}
            {!isAuthenticated && !userLoading && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-800">Guest Checkout</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      You're checking out as a guest. 
                      <button 
                        onClick={() => navigate('/login?redirect=/checkout')}
                        className="underline ml-1 font-medium"
                      >
                        Log in
                      </button>
                      {' '}to earn tokens and track your orders!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={billingInfo.name}
                    onChange={(e) => handleInputChange('billing', 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address * <span className="text-gray-500 font-normal">(for order confirmation)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={billingInfo.email}
                      onChange={(e) => handleInputChange('billing', 'email', e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="your@email.com"
                    />
                  </div>
                  {billingInfo.email && !isValidEmail(billingInfo.email) && (
                    <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={billingInfo.phone}
                    onChange={(e) => handleInputChange('billing', 'phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              
              
              {/* ============================================ */}
              {/* DELIVERY ADDRESS - Required for H3 location */}
              {/* ============================================ */}
              {deliveryMethod === 'delivery' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold mb-3 text-blue-800">Delivery Address</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        value={billingInfo.deliveryAddress.address}
                        onChange={(e) => setBillingInfo(prev => ({
                          ...prev,
                          deliveryAddress: { ...prev.deliveryAddress, address: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="123 Main St, Apt 4"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City *
                        </label>
                        <input
                          type="text"
                          value={billingInfo.deliveryAddress.city}
                          onChange={(e) => setBillingInfo(prev => ({
                            ...prev,
                            deliveryAddress: { ...prev.deliveryAddress, city: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Sacramento"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State *
                        </label>
                        <input
                          type="text"
                          value={billingInfo.deliveryAddress.state}
                          onChange={(e) => setBillingInfo(prev => ({
                            ...prev,
                            deliveryAddress: { ...prev.deliveryAddress, state: e.target.value.toUpperCase() }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="CA"
                          maxLength={2}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="w-1/2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        value={billingInfo.deliveryAddress.zip}
                        onChange={(e) => setBillingInfo(prev => ({
                          ...prev,
                          deliveryAddress: { ...prev.deliveryAddress, zip: e.target.value.replace(/\D/g, '').slice(0, 5) }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="95814"
                        maxLength={5}
                        required
                      />
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Your address helps us connect you with nearby buyers for group savings
                  </p>
                </div>
              )}
              

              {/* ============================================ */}
              {/* DELIVERY METHOD - ENHANCED FOR 4.7 */}
              {/* ============================================ */}
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Delivery Method</h3>
                <div className="space-y-3">
                  
                  {/* PICKUP OPTION - Enhanced with prominent combined savings */}
                  <label className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    deliveryMethod === 'pickup' 
                      ? 'border-green-500 bg-green-50 shadow-md' 
                      : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="pickup"
                          checked={deliveryMethod === 'pickup'}
                          onChange={(e) => setDeliveryMethod(e.target.value)}
                          className="mt-1 mr-3"
                        />
                        <div className={`p-2 rounded-lg mr-3 ${
                          deliveryMethod === 'pickup' ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <MapPin size={20} className={deliveryMethod === 'pickup' ? 'text-green-600' : 'text-gray-500'} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">Pickup</span>
                            {deliveryMethod === 'pickup' && (
                              <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                                BEST VALUE
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">Collect from local community hub</p>
                        </div>
                      </div>
                      
                      {/* Savings Display */}
                      <div className="text-right">
                        <div className="text-green-600 font-bold text-lg">FREE</div>
                        <div className="text-xs text-gray-400 line-through">$5.99</div>
                      </div>
                    </div>
                    
                    {/* Pickup Benefits Banner */}
                    <div className={`mt-3 p-3 rounded-lg ${
                      deliveryMethod === 'pickup' 
                        ? 'bg-gradient-to-r from-green-100 to-yellow-100 border border-green-200' 
                        : 'bg-gray-50 border border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Delivery Savings */}
                          <div className="flex items-center gap-1.5">
                            <Gift size={16} className="text-green-600" />
                            <span className="text-sm font-medium text-green-700">
                              Save ${STANDARD_DELIVERY_FEE.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Token Bonus - Only for authenticated users */}
                          {isAuthenticated && (
                            <>
                              <span className="text-gray-300">+</span>
                              <div className="flex items-center gap-1.5">
                                <Coins size={16} className="text-yellow-600" />
                                <span className="text-sm font-medium text-yellow-700">
                                  +{tokenCalc.calculatedPickupBonus} tokens
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Total Savings */}
                        <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                          deliveryMethod === 'pickup'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {isAuthenticated 
                            ? `$${(STANDARD_DELIVERY_FEE + tokenCalc.calculatedPickupBonus * 0.10).toFixed(2)} value`
                            : `$${STANDARD_DELIVERY_FEE.toFixed(2)} saved`
                          }
                        </div>
                      </div>
                    </div>
                  </label>
                  
                  {/* DELIVERY OPTION */}
                  <label className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    deliveryMethod === 'delivery' 
                      ? 'border-blue-500 bg-blue-50 shadow-md' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="delivery"
                          checked={deliveryMethod === 'delivery'}
                          onChange={(e) => setDeliveryMethod(e.target.value)}
                          className="mt-1 mr-3"
                        />
                        <div className={`p-2 rounded-lg mr-3 ${
                          deliveryMethod === 'delivery' ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <Truck size={20} className={deliveryMethod === 'delivery' ? 'text-blue-600' : 'text-gray-500'} />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">Delivery</span>
                          <p className="text-sm text-gray-500 mt-0.5">2-3 business days to your door</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 line-through text-sm">$5.99</span>
                          <span className="text-green-600 font-bold">FREE</span>
                        </div>
                        <div className="text-xs text-green-600 font-medium mt-0.5">
                          🎉 Launch promo!
                        </div>
                      </div>
                    </div>
                    
                    {/* Delivery Comparison Note - Shows what they're missing */}
                    {deliveryMethod === 'delivery' && isAuthenticated && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Sparkles size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-yellow-800">
                            <strong>Tip:</strong> Switch to pickup to earn <strong>+{tokenCalc.calculatedPickupBonus} bonus tokens</strong> (${(tokenCalc.calculatedPickupBonus * 0.10).toFixed(2)} value)!
                          </p>
                        </div>
                      </div>
                    )}
                  </label>
                </div>
                
                {/* Pickup Explanation - Shows when pickup selected */}
                {deliveryMethod === 'pickup' && (
                  <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Sparkles size={20} className="text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-green-800 mb-1">Great choice! Here's what you're getting:</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li className="flex items-center gap-2">
                            <Check size={14} className="text-green-600" />
                            <span><strong>${STANDARD_DELIVERY_FEE.toFixed(2)} delivery fee</strong> waived</span>
                          </li>
                          {isAuthenticated && (
                            <li className="flex items-center gap-2">
                              <Check size={14} className="text-green-600" />
                              <span><strong>{tokenCalc.pickupBonus} bonus tokens</strong> (${tokenCalc.pickupBonusValue.toFixed(2)} value)</span>
                            </li>
                          )}
                          <li className="flex items-center gap-2">
                            <Check size={14} className="text-green-600" />
                            <span>Supports community logistics hubs</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <Check size={14} className="text-green-600" />
                            <span>Reduces carbon footprint 🌱</span>
                          </li>
                        </ul>
                        {isAuthenticated && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <p className="text-sm font-semibold text-green-800">
                              Total pickup value: <span className="text-green-600">${tokenCalc.totalPickupSavings.toFixed(2)}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* END DELIVERY METHOD SECTION */}
            </div>
            
            {/* ============================================ */}
            {/* B15: TIME WINDOW SELECTION SECTION */}
            {/* ============================================ */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold">Delivery Time Windows</h3>
                  {/* Pulsing indicator */}
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
                  </span>
                </div>
                <button 
                  onClick={() => setShowTimeWindowInfo(!showTimeWindowInfo)}
                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                >
                  <Info size={16} />
                  {showTimeWindowInfo ? 'Hide' : 'Learn more'}
                </button>
              </div>
              
              {showTimeWindowInfo && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm">
                  <p className="text-blue-800 mb-2">
                    <strong>Why time windows matter:</strong>
                  </p>
                  <ul className="text-blue-700 space-y-1 ml-4 list-disc">
                    <li><strong>Longer windows = bigger savings</strong> - More time to pool with other buyers</li>
                    <li><strong>EXPRESS</strong> - Fastest, but fewer group discounts</li>
                    <li><strong>3-Day (Recommended)</strong> - Best balance of speed and savings</li>
                    <li><strong>Weekly</strong> - Maximum group discounts</li>
                  </ul>
                </div>
              )}
              
              {/* Mixed time windows warning */}
              {hasMixedTimeWindows && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={18} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">
                        Your cart has items with different delivery windows
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Items will be grouped by time window. You can change windows below.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Items grouped by time window */}
              <div className="space-y-3">
                {cartItems.map((item, idx) => {
                  const itemPrice = item.price || item.unitPrice || item.currentPrice || item.basePrice || item.productPrice || 0;
                  const itemQty = item.quantity || item.qty || 1;
                  const lineTotal = itemPrice * itemQty;
                  const itemName = item.name || item.title || item.productName || item.product?.name || 'Item';
                  const itemTimeWindow = item.timeWindow || 'THREE_DAY';
                  const timeWindowConfig = TIME_WINDOWS[itemTimeWindow] || TIME_WINDOWS.THREE_DAY;
                  const isEditing = editingTimeWindow === (item.id || item.productId || idx);
                  
                  return (
                    <div 
                      key={item.id || item.productId || idx} 
                      className="border rounded-lg p-3 hover:border-blue-200 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <span className="font-medium text-gray-800">{itemName}</span>
                          <div className="text-sm text-gray-500">
                            {itemQty > 1 ? `${itemQty} × ` : ''}${itemPrice.toFixed(2)}
                          </div>
                        </div>
                        <span className="font-semibold">${lineTotal.toFixed(2)}</span>
                      </div>
                      
                      {/* Time window display/edit */}
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        {isEditing ? (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-600 font-medium">Select delivery window:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(TIME_WINDOWS).map(([key, config]) => (
                                <button
                                  key={key}
                                  onClick={() => handleTimeWindowChange(item.id || item.productId, item.groupId, key)}
                                  className={`p-2 rounded-lg border text-left transition-all ${
                                    key === itemTimeWindow
                                      ? 'border-blue-500 bg-blue-50'
                                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                                  }`}
                                >
                                  <div className="flex items-center gap-1">
                                    <span>{config.icon}</span>
                                    <span className="font-medium text-sm">{config.label}</span>
                                    {config.recommended && (
                                      <span className="text-xs bg-green-100 text-green-700 px-1 rounded">Best</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5">{config.description}</p>
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={() => setEditingTimeWindow(null)}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Calendar size={14} className="text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {timeWindowConfig.icon} {timeWindowConfig.label}
                              </span>
                              {timeWindowConfig.recommended && (
                                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => setEditingTimeWindow(item.id || item.productId || idx)}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Change
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Set all items to same window */}
              {cartItems.length > 1 && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-sm text-gray-600 mb-2">Set all items to:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(TIME_WINDOWS).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => {
                          cartItems.forEach(item => {
                            if (updateCartItemTimeWindow) {
                              updateCartItemTimeWindow(item.id || item.productId, item.groupId, key);
                            }
                          });
                          addNotification({
                            type: 'success',
                            title: 'Time Windows Updated',
                            message: `All items set to ${config.label}`,
                            showToast: true
                          });
                        }}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors flex items-center gap-1"
                      >
                        <span>{config.icon}</span>
                        <span>{config.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* END TIME WINDOW SECTION */}
            
            {/* Token Redemption Section - Only for authenticated users with balance */}
            {isAuthenticated && effectiveTokenBalance > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg shadow-sm p-4 border border-yellow-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-yellow-600" />
                    <h3 className="font-semibold text-yellow-800">Use Your Tokens</h3>
                    <RedemptionCapTooltip />
                  </div>
                  <button 
                    onClick={() => setShowTokenInfo(!showTokenInfo)}
                    className="text-yellow-600 hover:text-yellow-700"
                  >
                    <Info size={18} />
                  </button>
                </div>
                
                {/* TRUE HYBRID TIER BADGE - Shows correct % per tier */}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${getTierColor(userLoyaltyTier)}`}>
                    <Award size={14} />
                    <span className="font-medium text-sm">
                      {tierCapInfo.label}: {Math.round((tierCapInfo.percent || 0.10) * 100)}% / ${tierCapInfo.maxUSD === Infinity ? '∞' : '$' + tierCapInfo.maxUSD} cap
                    </span>
                  </div>
                  <LoyaltyTierTooltip tier={userLoyaltyTier} />
                </div>
                
                
                {showTokenInfo && (
                  <div className="bg-white rounded-lg p-3 mb-3 text-sm">
                    <p className="text-gray-700 mb-2">
                      <strong>Token Value:</strong> 1 token = ${TOKEN_CONFIG.VALUE_PER_TOKEN.toFixed(2)}
                    </p>
                    <p className="text-gray-600 mb-2">
                      <strong>Your Balance:</strong> {formatTokens(effectiveTokenBalance)} tokens ({formatTokenValue(effectiveTokenBalance)})
                    </p>
                    <p className="text-gray-600 mb-2">
                      <strong>Your Level:</strong> {tierCapInfo.label} ({Math.round((tierCapInfo.percent || 0.10) * 100)}% tier)
                    </p>
                    <p className="text-gray-600 mb-2">
                      <strong>TRUE HYBRID:</strong> {Math.round((tierCapInfo.percent || 0.10) * 100)}% of cart OR ${tierCapInfo.maxUSD === Infinity ? 'Unlimited' : '$' + tierCapInfo.maxUSD} (whichever is LESS)
                    </p>
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        📊 <strong>This order:</strong> {Math.round((tierCapInfo.percent || 0.10) * 100)}% of ${subtotalWithDelivery.toFixed(2)} = ${(subtotalWithDelivery * (tierCapInfo.percent || 0.10)).toFixed(2)}
                        {tierCapInfo.maxUSD !== Infinity && (subtotalWithDelivery * (tierCapInfo.percent || 0.10)) > tierCapInfo.maxUSD && (
                          <span className="text-orange-600"> → capped at ${tierCapInfo.maxUSD}</span>
                        )}
                      </p>
                    </div>
                    {nextTierInfo && (
                      <p className="text-blue-600 text-xs mt-2">
                        💡 {nextTierInfo.message}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Balance and Max Display */}
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <span className="text-sm text-gray-600">Your balance: </span>
                    <span className="font-bold text-yellow-700">{formatTokens(effectiveTokenBalance)} tokens</span>
                    <span className="text-xs text-gray-500 ml-1">({formatTokenValue(effectiveTokenBalance)})</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-600">Max redeemable: </span>
                    <span className="font-bold text-green-600">{formatTokens(maxRedeemableTokens)}</span>
                    <span className="text-xs text-gray-500 ml-1">(${tokensToUSD(maxRedeemableTokens).toFixed(2)})</span>
                  </div>
                </div>
                
                {/* Token Slider - Enhanced for better draggability */}
                {maxRedeemableTokens > 0 ? (
                  <div className="mb-3">
                    <input
                      type="range"
                      min="0"
                      max={maxRedeemableTokens}
                      value={tokensToRedeem}
                      onChange={handleTokenSliderChange}
                      className="w-full h-3 bg-yellow-200 rounded-lg appearance-none cursor-pointer 
                                 [&::-webkit-slider-thumb]:appearance-none 
                                 [&::-webkit-slider-thumb]:w-6 
                                 [&::-webkit-slider-thumb]:h-6 
                                 [&::-webkit-slider-thumb]:rounded-full 
                                 [&::-webkit-slider-thumb]:bg-yellow-500 
                                 [&::-webkit-slider-thumb]:cursor-pointer
                                 [&::-webkit-slider-thumb]:shadow-md
                                 [&::-webkit-slider-thumb]:border-2
                                 [&::-webkit-slider-thumb]:border-yellow-600
                                 [&::-moz-range-thumb]:w-6 
                                 [&::-moz-range-thumb]:h-6 
                                 [&::-moz-range-thumb]:rounded-full 
                                 [&::-moz-range-thumb]:bg-yellow-500 
                                 [&::-moz-range-thumb]:cursor-pointer
                                 [&::-moz-range-thumb]:border-2
                                 [&::-moz-range-thumb]:border-yellow-600"
                      style={{ touchAction: 'none' }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>0</span>
                      <span className="font-medium text-yellow-700">
                        {tokensToRedeem > 0 ? `${formatTokens(tokensToRedeem)} selected` : 'Drag to select'}
                      </span>
                      <span>{formatTokens(maxRedeemableTokens)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 p-3 bg-gray-100 rounded-lg text-center text-sm text-gray-600">
                    {effectiveTokenBalance === 0 
                      ? "You don't have any tokens to redeem yet."
                      : `Cart total too low for token redemption at your ${tierCapInfo.label} tier.`
                    }
                  </div>
                )}
                
                {/* Quick Select Buttons */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => handleQuickSelect('none')}
                    className={`flex-1 py-2 text-sm rounded-lg border font-medium transition-colors ${
                      tokensToRedeem === 0 
                        ? 'bg-gray-200 border-gray-400 text-gray-700' 
                        : 'border-gray-300 hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    None
                  </button>
                  <button
                    onClick={() => handleQuickSelect('half')}
                    disabled={maxRedeemableTokens === 0}
                    className={`flex-1 py-2 text-sm rounded-lg border font-medium transition-colors ${
                      tokensToRedeem === Math.floor(maxRedeemableTokens / 2) && maxRedeemableTokens > 0
                        ? 'bg-yellow-200 border-yellow-400 text-yellow-800'
                        : maxRedeemableTokens === 0
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 hover:bg-yellow-50 text-gray-600'
                    }`}
                  >
                    Half
                  </button>
                  <button
                    onClick={() => handleQuickSelect('max')}
                    disabled={maxRedeemableTokens === 0}
                    className={`flex-1 py-2 text-sm rounded-lg border font-medium transition-colors ${
                      tokensToRedeem === maxRedeemableTokens && maxRedeemableTokens > 0
                        ? 'bg-yellow-200 border-yellow-400 text-yellow-800'
                        : maxRedeemableTokens === 0
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 hover:bg-yellow-50 text-gray-600'
                    }`}
                  >
                    Max
                  </button>
                </div>
                
                {/* Current Redemption Display */}
                {tokensToRedeem > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700 font-medium">
                        Using {formatTokens(tokensToRedeem)} tokens
                      </span>
                      <span className="text-green-700 font-bold text-lg">
                        -${tokenDiscount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Next Tier Upsell - Show when user could redeem more with higher tier */}
                {nextTierInfo && redemptionInfo?.limitedByCap && (
                  <div className="mt-3 text-sm text-blue-600 bg-blue-50 rounded-lg p-2 flex items-start gap-2">
                    <ChevronRight size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{nextTierInfo.message}</span>
                  </div>
                )}
              </div>
            )}

            {/* Guest Token CTA */}
            {!isAuthenticated && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Coins className="w-5 h-5 text-yellow-600" />
                  <h3 className="font-medium text-yellow-800">Earn Rewards!</h3>
                </div>
                <p className="text-sm text-yellow-700">
                  Create an account to earn <strong>{formatTokens(tokenCalc.totalTokens)} tokens</strong> ({formatTokenValue(tokenCalc.totalTokens)}) on this purchase!
                </p>
                <button
                  onClick={() => navigate('/register?redirect=/checkout')}
                  className="mt-3 w-full bg-yellow-500 text-white py-2 rounded-lg font-medium hover:bg-yellow-600 transition-colors"
                >
                  Create Account to Earn Tokens
                </button>
              </div>
            )}

            {/* Cart Summary Preview */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-medium mb-3">Order Summary</h3>
              
              {/* Individual Items */}
              <div className="space-y-2 mb-3 pb-3 border-b">
                {cartItems.map((item, idx) => {
                  const itemPrice = item.price || item.unitPrice || item.currentPrice || item.basePrice || item.productPrice || 0;
                  const itemQty = item.quantity || item.qty || 1;
                  // Apply bulk discount to match cart page and getCartTotals()
                  let discount = 0;
                  if (itemQty >= 5) discount = 0.05;
                  else if (itemQty >= 3) discount = 0.03;
                  const discountedPrice = itemPrice * (1 - discount);
                  const lineTotal = discountedPrice * itemQty;
                  const originalTotal = itemPrice * itemQty;
                  const itemName = item.name || item.title || item.productName || item.product?.name || 'Item';
                  
                  return (
                    <div key={item.id || item.productId || idx} className="flex justify-between text-sm">
                      <div className="flex-1">
                        <span className="text-gray-800">{itemName}</span>
                        <span className="text-gray-500 ml-2">
                          {itemQty > 1 ? `× ${itemQty}` : ''} @ ${itemPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium">${lineTotal.toFixed(2)}</span>
                        {discount > 0 && (
                          <span className="text-xs text-green-600 ml-1">(-{Math.round(discount * 100)}%)</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>

                {/* Platform Fee */}
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    Platform Fee <span className="text-xs text-gray-400">(7.5%)</span>
                  </span>
                  <span className="font-medium">${platformFee.toFixed(2)}</span>
                </div>

                {/* Logistics Fee */}
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    Logistics Fee <span className="text-xs text-gray-400">(7.5%)</span>
                  </span>
                  <span className="font-medium">${logisticsFee.toFixed(2)}</span>
                </div>

                {/* CA Sales Tax */}
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    CA Sales Tax <span className="text-xs text-gray-400">(7.25%)</span>
                  </span>
                  <span className="font-medium">${salesTax.toFixed(2)}</span>
                </div>
                
                {/* Delivery/Pickup Line - Enhanced */}
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {deliveryMethod === 'pickup' ? 'Pickup' : 'Delivery'}
                  </span>
                  <div className="text-right">
                    {deliveryMethod === 'pickup' ? (
                      <span className="text-green-600 font-medium">
                        FREE <span className="text-gray-400 line-through text-xs ml-1">${STANDARD_DELIVERY_FEE.toFixed(2)}</span>
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">FREE (promo)</span>
                    )}
                  </div>
                </div>
                
                {/* Pickup Bonus in Summary */}
                {deliveryMethod === 'pickup' && isAuthenticated && tokenCalc.pickupBonus > 0 && (
                  <div className="flex justify-between text-yellow-600 bg-yellow-50 -mx-4 px-4 py-1">
                    <span className="flex items-center gap-1">
                      <Coins size={14} />
                      Pickup bonus
                    </span>
                    <span className="font-medium">+{tokenCalc.pickupBonus} tokens</span>
                  </div>
                )}
                
                {tokenDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Token discount ({formatTokens(tokensToRedeem)} tokens)</span>
                    <span>-${tokenDiscount.toFixed(2)}</span>
                  </div>
                )}

                {/* Total */}
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Estimated Total</span>
                  <div className="text-right">
                    <span>${totalWithFees.toFixed(2)}</span>
                    
                  </div>
                </div>

                {isAuthenticated && (
                  <div className="pt-1">
                    <div className="flex justify-between text-yellow-600">
                      <span className="flex items-center gap-1">
                        <Star size={14} />
                        Tokens to earn
                      </span>
                      <span className="font-medium">+{formatTokens(tokenCalc.totalTokens)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {tokenCalc.baseTokens} base
                      {tokenCalc.bulkBonus > 0 && ` + ${tokenCalc.bulkBonus} bulk (${tokenCalc.bulkBonusPercent}%)`}
                      {tokenCalc.pickupBonus > 0 && ` + ${tokenCalc.pickupBonus} pickup (10%)`}
                    </p>
                  </div>
                )}
                {!isAuthenticated && tokenCalc.totalTokens > 0 && (
                  <div className="pt-1 text-sm text-gray-500">
                    <span>💡 Create an account to earn {formatTokens(tokenCalc.totalTokens)} tokens (${tokenCalc.totalValue.toFixed(2)} value)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Payment */}
        {currentStep === 2 && (
          <div className="m-4">
            {paymentClientSecret && isConfigured && (
              <Elements stripe={stripePromise} options={{ 
                clientSecret: paymentClientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#2563eb',
                  },
                },
              }}>
                <StripeCheckout 
                  amount={totalWithFees}
                  onSuccess={handlePaymentSuccess}
                  onError={(error) => {
                    console.error('Payment error:', error);
                    addNotification({
                      type: 'error',
                      title: 'Payment Failed',
                      message: error.message || 'Payment processing failed. Please try again.',
                      showToast: true
                    });
                  }}
                />
              </Elements>
            )}
            
            {/* Token Summary for Step 2 */}
            {tokensToRedeem > 0 && isAuthenticated && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Token Discount Applied</span>
                  </div>
                  <span className="font-bold text-green-700">-${tokenDiscount.toFixed(2)}</span>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  {formatTokens(tokensToRedeem)} tokens will be deducted from your balance
                </p>
              </div>
            )}
            
            {/* Pickup Bonus Reminder */}
            {deliveryMethod === 'pickup' && isAuthenticated && tokenCalc.pickupBonus > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-yellow-600" />
                    <span className="font-medium text-yellow-800">Pickup Bonus</span>
                  </div>
                  <span className="font-bold text-yellow-700">+{tokenCalc.pickupBonus} tokens</span>
                </div>
                <p className="text-sm text-yellow-600 mt-1">
                  You're earning extra tokens by choosing pickup!
                </p>
              </div>
            )}
            
            {/* Order details */}
            <div className="bg-white rounded-lg shadow-sm p-4 mt-4">
              <h3 className="font-medium mb-2">Order Details</h3>
              <div className="text-sm space-y-1 text-gray-600">
                <p><strong>Email:</strong> {billingInfo.email}</p>
                <p><strong>Method:</strong> {deliveryMethod === 'pickup' ? 'Pickup' : 'Delivery'}</p>
                <p><strong>Estimated Total:</strong> ${totalWithFees.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review Order - This step is now handled after payment */}
        {currentStep === 3 && (
          <div className="space-y-4 m-4">
            {/* Processing message */}
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-lg font-semibold mb-2">Processing Your Order...</h2>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </div>
          </div>
        )}

        {/* Step 4: Order Confirmation */}
        {currentStep === 4 && orderComplete && (
          <div className="m-4">
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              
              <h2 className="text-xl font-semibold mb-2">Order Confirmed!</h2>
              <p className="text-gray-600 mb-2">
                Thank you for your purchase.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                A confirmation email has been sent to <strong>{billingInfo.email}</strong>
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Order Total:</span>
                  <span className="font-semibold">${totalWithFees.toFixed(2)}</span>
                </div>
                {isAuthenticated && tokensToRedeem > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Tokens Used:</span>
                    <span className="font-semibold text-green-600">-{formatTokens(tokensToRedeem)}</span>
                  </div>
                )}
                {isAuthenticated && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tokens Earned:</span>
                      <span className="font-semibold text-yellow-600">+{formatTokens(tokenCalc.totalTokens)}</span>
                    </div>
                    {deliveryMethod === 'pickup' && tokenCalc.pickupBonus > 0 && (
                      <p className="text-xs text-green-600 text-right mt-1">
                        Includes +{tokenCalc.pickupBonus} pickup bonus!
                      </p>
                    )}
                  </>
                )}
              </div>
              
              {/* Create Account CTA for Guests */}
              {!isAuthenticated && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 font-medium mb-2">🎁 Don't miss out on rewards!</p>
                  <p className="text-sm text-yellow-700 mb-3">
                    Create an account to track your order and earn tokens on future purchases.
                  </p>
                  <button
                    onClick={() => navigate('/register')}
                    className="w-full bg-yellow-500 text-white py-2 rounded-lg font-medium hover:bg-yellow-600"
                  >
                    Create Account
                  </button>
                </div>
              )}
              
              <div className="space-y-3">
                {isAuthenticated && (
                  <button 
                    onClick={() => navigate('/orders')}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700"
                  >
                    View Order Status
                  </button>
                )}
                <button 
                  onClick={() => navigate('/home')}
                  className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* B14: Bottom Actions with Continue Shopping */}
        {/* ============================================ */}
        {currentStep === 1 && (
          <div className="sticky bottom-0 bg-white border-t p-4 mt-6">
            {/* Continue Shopping Link - B14 */}
            <div className="flex justify-center mb-3">
              <button
                onClick={() => navigate('/home')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Continue Shopping
              </button>
            </div>

            {/* Terms Agreement Notice */}
            <p className="text-xs text-gray-500 text-center mb-3">
              By completing this purchase, you agree to our{' '}
              <Link to="/terms" className="text-blue-600 hover:underline" target="_blank">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline" target="_blank">
                Privacy Policy
              </Link>
            </p>
            
            <button 
              onClick={handleNext}
              disabled={!validateStep() || loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Continue to Payment'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;